import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { ListingAgentResearchRunDto, ListingAgentResearchWorkspaceDto } from "@/api/listingAgentResearch";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Skeleton } from "@/design-system/components/ui";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { QueryErrorState } from "../../queryStates";
import { RUN_STATUS, agentResearchCopy, parseStage, type StageKey } from "./agentResearchCopy";
import { FindingsStage } from "./FindingsStage";
import { ResearchStage } from "./ResearchStage";
import { ReviewStage } from "./ReviewStage";
import { StageRail } from "./StageRail";
import { StartStage } from "./StartStage";
import { useAgentResearchRun, useAgentResearchWorkspace } from "./useAgentResearch";
import { YourTurnStage } from "./YourTurnStage";

/** The run a bare URL opens on: the active run, else the newest. */
export function defaultRunId(ws: ListingAgentResearchWorkspaceDto | undefined): string | null {
  return ws?.activeRunId ?? ws?.latestRun?.id ?? ws?.runs[0]?.id ?? null;
}

/** The stage a bare URL opens on: Start with no run, AI research while one is
 *  live, Findings once it has finished. */
export function defaultStage(runId: string | null, isActive: boolean | undefined): StageKey {
  if (!runId) return "start";
  return isActive ? "research" : "findings";
}

/**
 * Listing agent research — FIND AGENT / RESEARCH (D7–D18) on its own page (R6),
 * not a drawer: `/listings/{id}/agent-research`. `?run=` selects the run (the
 * bell links here with it); `?stage=` the stage. Both are read at parse time and
 * written with one `setSearchParams` per change.
 *
 * <p>Viewing needs `Listings.Read` (the route); every action is `Listings.Manage`
 * only and absent — never greyed — without it.</p>
 */
export function ListingAgentResearchPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const auth = useOptionalAuth();
  const canManage = Boolean(auth?.hasPermission("Listings.Manage"));
  const { date } = useDateFormatter();

  const workspace = useAgentResearchWorkspace(id);
  const ws = workspace.data;
  const runId = params.get("run") || defaultRunId(ws);
  const run = useAgentResearchRun(id, runId);
  const runSummary = ws?.runs.find((r) => r.id === runId);
  const stage = parseStage(params.get("stage")) ?? defaultStage(runId, run.data?.isActive ?? runSummary?.isActive);

  useBreadcrumbs(
    ws
      ? [
          { label: "Listings", href: "/listings" },
          { label: ws.facts.displayName, href: `/listings/${id}` },
          { label: "Agent research" },
        ]
      : null,
  );

  const select = (next: { run?: string; stage?: StageKey }) => {
    const p = new URLSearchParams(params);
    if (next.run !== undefined) p.set("run", next.run);
    if (next.stage !== undefined) p.set("stage", next.stage);
    setParams(p, { replace: true });
  };
  const goToRun = (r: ListingAgentResearchRunDto) => select({ run: r.id, stage: "research" });

  if (workspace.isLoading) return <Skeleton className="h-space-6 w-full" />;
  if (workspace.isError || !ws) {
    return (
      <QueryErrorState what="the agent research" onRetry={() => void workspace.refetch()} retrying={workspace.isFetching} />
    );
  }

  const pending = run.data?.findingGroups.reduce(
    (n, g) => n + g.findings.filter((f) => f.decision === "Pending" && !f.savedAtUtc).length,
    0,
  );

  return (
    <div className="grid gap-space-5" data-testid="agent-research-page">
      <PageHeader
        eyebrow={agentResearchCopy.eyebrow}
        title={ws.facts.displayName}
        actions={
          <Link
            to={`/listings/${id}`}
            className="flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
            data-testid="agent-research-back"
          >
            <ArrowLeft size={13} strokeWidth={1.5} aria-hidden /> {agentResearchCopy.backToListing}
          </Link>
        }
      />

      {ws.runs.length > 0 && (
        <label className="flex flex-wrap items-center gap-space-2 text-small">
          <span className="text-gray-mid">Research run</span>
          <select
            className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
            value={runId ?? ""}
            onChange={(e) => select({ run: e.target.value })}
            data-testid="agent-research-run-select"
          >
            {ws.runs.map((r) => (
              <option key={r.id} value={r.id}>
                {date(r.queuedAt)} — {RUN_STATUS[r.status]?.label ?? r.status}
                {r.findingCount ? ` — ${r.findingCount} found` : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      <StageRail
        current={stage}
        onSelect={(s) => select({ stage: s })}
        badges={pending ? { findings: `${pending} to review` } : undefined}
      />

      {runId && run.isError && (
        <QueryErrorState what="this research run" onRetry={() => void run.refetch()} retrying={run.isFetching} />
      )}

      {stage === "start" && (
        <StartStage
          workspace={ws}
          canManage={canManage}
          onStarted={goToRun}
          onWatchActive={(active) => select({ run: active, stage: "research" })}
        />
      )}
      {stage === "research" && <ResearchStage run={run.data} />}
      {stage === "findings" && <FindingsStage listingId={id} run={run.data} canManage={canManage} />}
      {stage === "your-turn" && (
        <YourTurnStage
          listingId={id}
          run={run.data}
          canManage={canManage}
          hasActiveRun={Boolean(ws.activeRunId)}
          onRanAgain={goToRun}
        />
      )}
      {stage === "review" && <ReviewStage listingId={id} run={run.data} canManage={canManage} />}
    </div>
  );
}
